import { useEffect, useState } from "react";
import useSWR from "swr";
import { getFetcher } from "../services/api";

/**
 * Pemilihan periode untuk halaman pelaporan.
 *
 * Menggantikan pola lama yang diulang di setiap hook:
 *   ambil /api/period/actives, lalu pakai data[0] sebagai default.
 *
 * Sekarang default-nya adalah periode aktif TERLAMA yang laporannya belum
 * selesai untuk perusahaan tersebut. Penentuannya dilakukan di backend karena
 * bergantung pada isi tabel pelaporan masing-masing modul.
 *
 * @param companyId    perusahaan yang sedang dibuka
 * @param resourcePath basis endpoint modul, mis. "pelaporan-bahan-b3-distribusi".
 *                     Kosongkan untuk memakai perilaku lama (periode aktif pertama).
 */
export default function useActivePeriodSelection(companyId, resourcePath) {
  const [selectedPeriod, setSelectedPeriod] = useState(null);

  const { data: dataPeriods, isLoading: isLoadingPeriods } = useSWR(`/api/period/actives`, getFetcher);

  const { data: dataDefaultPeriod, isLoading: isLoadingDefaultPeriod } = useSWR(
    companyId && resourcePath ? `/api/${resourcePath}/active-unsubmitted-period/${companyId}` : null,
    getFetcher,
  );

  useEffect(() => {
    if (selectedPeriod) return;

    const unsubmitted = dataDefaultPeriod?.data;
    if (unsubmitted?.id) {
      setSelectedPeriod({ label: unsubmitted.name, value: unsubmitted.id });
      return;
    }

    // Selama query periode default masih berjalan, jangan memilih apa pun dulu.
    // Tanpa jeda ini, periode aktif pertama sempat terpilih lalu tergeser
    // beberapa saat kemudian - tabel ikut berkedip mengikuti pergeseran itu.
    if (companyId && resourcePath && isLoadingDefaultPeriod) return;

    // Fallback ke perilaku lama, supaya halaman tetap berfungsi meski endpoint
    // periode belum tersedia di backend.
    if (dataPeriods?.data?.length > 0) {
      const firstPeriod = dataPeriods.data[0];
      setSelectedPeriod({ label: firstPeriod.name, value: firstPeriod.id });
    }
  }, [dataPeriods, dataDefaultPeriod, isLoadingDefaultPeriod, companyId, resourcePath, selectedPeriod]);

  return {
    selectedPeriod,
    setSelectedPeriod,
    dataPeriods,
    isLoadingPeriods: isLoadingPeriods || isLoadingDefaultPeriod,
    // Nilai "alasan" dari backend: BELUM_ADA_LAPORAN, LAPORAN_BELUM_SELESAI,
    // SEMUA_SUDAH_SELESAI, atau TIDAK_ADA_PERIODE_AKTIF. Berguna kalau nanti
    // ingin menampilkan keterangan di UI.
    alasanPeriodeDefault: dataDefaultPeriod?.alasan ?? null,
  };
}